import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Dashboard as MatrixIcon,
  TrendingUp as GrowthIcon,
  Person as PersonIcon,
  Assignment as TaskIcon
} from '@mui/icons-material';
import axios from 'axios';
import { apiUrl } from '../../config/api';

interface Task {
  id: number;
  title: string;
  description: string;
  urgency: number;
  importance: number;
  growth_value: number;
  status: string;
  assigned_to?: number;
  member_name?: string;
  member_role?: string;
  goal_title?: string;
}

interface MatrixQuadrant {
  title: string;
  subtitle: string;
  color: string;
  bgcolor: string;
  tasks: Task[];
  icon: React.ReactNode;
}

interface TaskStats {
  totalTasks: number;
  averageGrowthValue: number;
  highValueTasks: number;
  delegatedTasks: number;
}

const EisenhowerMatrix: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterMember, setFilterMember] = useState<string>('all');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [stats, setStats] = useState<TaskStats>({
    totalTasks: 0,
    averageGrowthValue: 0,
    highValueTasks: 0,
    delegatedTasks: 0
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl('/api/tasks'));
      setTasks(response.data);
    } catch (err) {
      setError('Failed to load tasks');
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const totalTasks = tasks.length;
    const averageGrowthValue = totalTasks > 0 
      ? tasks.reduce((sum, task) => sum + task.growth_value, 0) / totalTasks 
      : 0;
    const highValueTasks = tasks.filter(task => task.growth_value >= 4).length;
    const delegatedTasks = tasks.filter(task => task.assigned_to).length;

    setStats({
      totalTasks,
      averageGrowthValue: Math.round(averageGrowthValue * 10) / 10,
      highValueTasks,
      delegatedTasks
    });
  };

  const getQuadrants = (): MatrixQuadrant[] => {
    const filteredTasks = filterMember === 'all' 
      ? tasks 
      : tasks.filter(task => task.member_name === filterMember);

    return [
      {
        title: 'DO',
        subtitle: 'Important & Urgent',
        color: '#d32f2f',
        bgcolor: '#ffebee',
        icon: <TaskIcon />,
        tasks: filteredTasks.filter(task => task.importance >= 3 && task.urgency >= 3)
      },
      {
        title: 'DECIDE',
        subtitle: 'Important & Not Urgent',
        color: '#1976d2',
        bgcolor: '#e3f2fd',
        icon: <GrowthIcon />,
        tasks: filteredTasks.filter(task => task.importance >= 3 && task.urgency < 3)
      },
      {
        title: 'DELEGATE',
        subtitle: 'Not Important & Urgent',
        color: '#f57c00',
        bgcolor: '#fff3e0',
        icon: <PersonIcon />,
        tasks: filteredTasks.filter(task => task.importance < 3 && task.urgency >= 3)
      },
      {
        title: 'DELETE',
        subtitle: 'Not Important & Not Urgent',
        color: '#616161',
        bgcolor: '#f5f5f5',
        icon: <MatrixIcon />,
        tasks: filteredTasks.filter(task => task.importance < 3 && task.urgency < 3)
      }
    ];
  };

  const getGrowthValueColor = (value: number) => {
    if (value >= 4) return '#4caf50';
    if (value >= 3) return '#ff9800';
    return '#f44336';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      case 'in-progress': return 'primary';
      case 'on-hold': return 'warning';
      default: return 'default';
    }
  };

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    setFilterMember(event.target.value);
  };

  const uniqueMembers = Array.from(
    new Set(tasks.map(task => task.member_name).filter(Boolean))
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const quadrants = getQuadrants();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Enhanced Eisenhower Matrix
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Member</InputLabel>
            <Select
              value={filterMember}
              onChange={handleFilterChange}
              label="Filter by Member"
            >
              <MenuItem value="all">All Members</MenuItem>
              {uniqueMembers.map(member => (
                <MenuItem key={member} value={member}>{member}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTasks}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tasks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <MatrixIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No tasks available for analysis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Add some tasks first to see the Eisenhower Matrix analysis and prioritization insights.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {/* Statistics Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {stats.totalTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Tasks
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="secondary">
                  {stats.averageGrowthValue}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Growth Value
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#4caf50' }}>
                  {stats.highValueTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  High Value Tasks
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#ff9800' }}>
                  {stats.delegatedTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Delegated Tasks
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Matrix */}
          <Paper sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Priority Matrix
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tasks categorized by urgency (vertical) and importance (horizontal)
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, height: '600px' }}>
              {quadrants.map((quadrant, index) => (
                <Box key={index} sx={{ flex: '1 1 45%', minWidth: '300px' }}>
                  <Paper
                    sx={{
                      height: '280px',
                      p: 2,
                      bgcolor: quadrant.bgcolor,
                      border: `2px solid ${quadrant.color}`,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {/* Quadrant Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: quadrant.color, mr: 1, width: 32, height: 32 }}>
                        {quadrant.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ color: quadrant.color, fontWeight: 'bold' }}>
                          {quadrant.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {quadrant.subtitle} ({quadrant.tasks.length} tasks)
                        </Typography>
                      </Box>
                    </Box>

                    {/* Tasks */}
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                      {quadrant.tasks.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                          No tasks in this quadrant
                        </Typography>
                      ) : (
                        quadrant.tasks.map(task => (
                          <Card
                            key={task.id}
                            sx={{
                              mb: 1,
                              cursor: 'pointer',
                              '&:hover': { boxShadow: 2 },
                              border: selectedTask?.id === task.id ? `2px solid ${quadrant.color}` : 'none'
                            }}
                            onClick={() => setSelectedTask(task)}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {task.title.length > 30 ? task.title.substring(0, 30) + '...' : task.title}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Chip
                                  label={`Growth: ${task.growth_value}/5`}
                                  size="small"
                                  sx={{
                                    bgcolor: getGrowthValueColor(task.growth_value),
                                    color: 'white',
                                    fontSize: '0.7rem'
                                  }}
                                />
                                <Chip
                                  label={task.status}
                                  size="small"
                                  color={getStatusColor(task.status) as any}
                                  variant="outlined"
                                />
                              </Box>

                              {task.member_name && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                  <Avatar sx={{ width: 16, height: 16, fontSize: '0.7rem', mr: 0.5 }}>
                                    {task.member_name.charAt(0)}
                                  </Avatar>
                                  <Typography variant="caption" color="text.secondary">
                                    {task.member_name}
                                  </Typography>
                                </Box>
                              )}

                              <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Urgency Level">
                                  <Chip
                                    label={`U:${task.urgency}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem' }}
                                  />
                                </Tooltip>
                                <Tooltip title="Importance Level">
                                  <Chip
                                    label={`I:${task.importance}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem' }}
                                  />
                                </Tooltip>
                              </Box>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </Box>
                  </Paper>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Task Details Panel */}
          {selectedTask && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Task Details
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '2 1 300px' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {selectedTask.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {selectedTask.description}
                    </Typography>
                    {selectedTask.goal_title && (
                      <Typography variant="body2">
                        <strong>Related Goal:</strong> {selectedTask.goal_title}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Urgency:</Typography>
                        <Chip label={`${selectedTask.urgency}/5`} size="small" color="error" />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Importance:</Typography>
                        <Chip label={`${selectedTask.importance}/5`} size="small" color="primary" />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Growth Value:</Typography>
                        <Chip 
                          label={`${selectedTask.growth_value}/5`} 
                          size="small" 
                          sx={{ 
                            bgcolor: getGrowthValueColor(selectedTask.growth_value),
                            color: 'white'
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Status:</Typography>
                        <Chip 
                          label={selectedTask.status} 
                          size="small" 
                          color={getStatusColor(selectedTask.status) as any}
                        />
                      </Box>
                      {selectedTask.member_name && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Assigned to:</Typography>
                          <Typography variant="body2">{selectedTask.member_name}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Container>
  );
};

export default EisenhowerMatrix;